import React from 'react';
import {FormattedMessage} from 'react-intl';
import {alpha, useTheme} from '@mui/material/styles';

function IdIcon({size = 24}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
      <path d="M9 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
      <path d="M6 16a3 3 0 0 1 6 0" />
      <path d="M15 9h4" /><path d="M15 13h4" />
    </svg>
  );
}

function LockIcon({size = 16}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function ArrowIcon({size = 16}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function SparkIcon({size = 11}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
    </svg>
  );
}

/**
 * IdentityBox — "returning customer" quick identification CTA.
 * Shows desktop layout (inline) and mobile layout (full-width).
 * Travel-only component.
 */
const IdentityBox = ({onIdentify}) => {
  const theme = useTheme();
  return <React.Fragment>
    {/* DESKTOP */}
    <div className="id-desktop" style={{
      display: 'flex', alignItems: 'center', gap: 16,
      background: theme.palette.action.selected, border: `1px solid ${theme.palette.divider}`,
      borderRadius: 'var(--wobi-v2-radius-lg)', padding: '14px 16px',
    }}>
      <span style={{
        width: 46, height: 46, flex: 'none', borderRadius: '50%',
        background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.secondary.main, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <IdIcon size={24} />
      </span>
      <div style={{flex: 1, textAlign: 'right', minWidth: 0}}>
        <div style={{fontWeight: theme.typography.fontWeightBold, fontSize: theme.typography.pxToRem(15), color: theme.palette.text.primary}}>
          <FormattedMessage id='identity.returningCustomer'/>
        </div>
        <div style={{fontSize: theme.typography.pxToRem(12.5), color: theme.palette.text.secondary, lineHeight: 1.45, marginTop: 2}}>
          <FormattedMessage id='identity.desktopDescription'/>
        </div>
      </div>
      <button
        type="button"
        onClick={onIdentify}
        style={{
          flex: 'none', height: 44, padding: '0 26px', cursor: 'pointer',
          border: 'none', borderRadius: 'var(--wobi-v2-radius-full)',
          background: theme.palette.secondary.main, color: theme.palette.secondary.contrastText,
          fontWeight: theme.typography.fontWeightBold, fontSize: theme.typography.pxToRem(15),
          boxShadow: `0 6px 14px ${alpha(theme.palette.secondary.main, 0.22)}`,
        }}
      >
        <FormattedMessage id='identity.identify'/>
      </button>
    </div>

    {/* MOBILE */}
    <div className="id-mobile" style={{
      display: 'none', position: 'relative', overflow: 'hidden',
      background: `linear-gradient(115deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.selected} 100%)`,
      border: `1px solid ${theme.palette.divider}`, borderRadius: 'var(--wobi-v2-radius-xl)',
      padding: '15px 16px', boxShadow: theme.shadows[2],
    }}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8}}>
        <span style={{fontWeight: theme.typography.fontWeightBlack, fontSize: theme.typography.pxToRem(15.5), color: theme.palette.text.primary}}>
          <FormattedMessage id='identity.returningCustomer'/>
        </span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none',
          background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, color: theme.palette.secondary.dark,
          borderRadius: 'var(--wobi-v2-radius-full)', padding: '2px 9px',
          fontWeight: theme.typography.fontWeightBold, fontSize: theme.typography.pxToRem(11),
        }}>
          <SparkIcon size={11} /> <FormattedMessage id='identity.autoFill'/>
        </span>
      </div>
      <div style={{fontSize: theme.typography.pxToRem(12.5), color: theme.palette.text.secondary, lineHeight: 1.5, marginTop: 4, textAlign: 'right'}}>
        <FormattedMessage id='identity.mobileDescription'/>
      </div>
      <button
        type="button"
        onClick={onIdentify}
        style={{
          width: '100%', height: 46, marginTop: 12, cursor: 'pointer',
          border: 'none', borderRadius: 'var(--wobi-v2-radius-full)',
          background: theme.palette.secondary.main, color: theme.palette.secondary.contrastText,
          fontWeight: theme.typography.fontWeightBold, fontSize: theme.typography.pxToRem(15),
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          boxShadow: `0 8px 18px ${alpha(theme.palette.secondary.main, 0.26)}`,
        }}
      >
        <LockIcon size={16} /> <FormattedMessage id='identity.fastIdentify'/>
        <span style={{display: 'inline-flex', transform: 'scaleX(-1)', opacity: 0.85}}>
          <ArrowIcon size={16} />
        </span>
      </button>
    </div>

    <style>{`@media (max-width:620px){.id-desktop{display:none !important}.id-mobile{display:block !important}}`}</style>
  </React.Fragment>;
};

export default IdentityBox;
